<script lang="ts">
  import type { Source } from '../../modules/source/source.interface';
  import type { CategoryConfig } from '../../modules/projects/project.interface';
  import { CategoryPropType } from '../../modules/projects/project.interface';
  import { Dictionary, omit, set } from 'lodash';
  import { Row, Col, TextField } from 'svelte-materialify';
  import { CategoryDisplayType } from '../../modules/projects/project.interface';
  import { ItemService } from '../../modules';
  import { flatten } from '../../lib/utils';
  import Select from '../Select.svelte';
  import ItemPreview from '../item/ItemPreview.svelte';
  import ColorPicker from '../ColorPicker.svelte';
  import { ProjectConfigService } from '../../modules';

  export let source: Source;
  export let articles: Dictionary<any>[];
  export let mapping: Dictionary<any> = getMapping(source);

  let flattenArticles: Dictionary<any>[] = [];

  $: categoryConfig = ProjectConfigService.getCategoryConfig(
    source.project,
    source.category
  );
  $: prepareArticles(articles);
  $: sampleItems = generateItemsByPropMap(mapping);
  $: previewCols = getPreviewCols(categoryConfig);

  async function prepareArticles(articles: Dictionary<any>[]) {
    flattenArticles = articles.map((sampleArticle) => {
      const article = omit(sampleArticle, ['flags']);

      return flatten({ ...article, $source: source });
    });
  }

  function getPreviewCols(categoryConfig: CategoryConfig) {
    const colsMap = {
      6: [CategoryDisplayType.IMAGE],
      12: [CategoryDisplayType.TEXT],
    };

    const cols = Object.keys(colsMap).find((colsKey) =>
      colsMap[colsKey].includes(categoryConfig.displayType)
    );

    return parseInt(cols);
  }

  function generateItemsByPropMap(propMap: Dictionary<any>) {
    const sampleItems = flattenArticles.map((flattenArticle) => {
      const item = ItemService.createDefaultItem(source.project, source.category);

      Object.keys(propMap).forEach((propKey) => {
        set(item, propKey, flattenArticle[propMap[propKey]]);
      });

      return item;
    });

    return sampleItems;
  }

  function getMapping(source: Source) {
    const mappings = {
      instagram: {
        ...getPandaMapping(),
        'details.isGallery': 'source.isGallery',
        'details.sourceIcon': 'source.icon',
      },
    };

    const mapping = mappings[source.crawlerOptions.type];

    return mapping || getPandaMapping();
  }

  function getPandaMapping() {
    return {
      title: 'title',
      image: 'image.normal',
      url: 'source.targetUrl',
      candidateId: 'source.id',
    };
  }
</script>

{#if flattenArticles.length}
  <Row>
    <Col cols={6}>
      <h5 class="text-h5 mb-4 mt-4">Mapping</h5>
      {#each categoryConfig.props as prop}
        {#if prop.isFixed}
          {#if prop.type === CategoryPropType.COLORS}
            <h5 class="text-h6 mb-4">{prop.name}</h5>
            <ColorPicker bind:color={mapping[`#${prop.key}`]} />
          {:else}
            <TextField bind:value={mapping[`#${prop.key}`]}
              >{prop.isOptional ? '' : '*'} {prop.name}</TextField
            >
          {/if}
        {:else}
          <Select
            className="mt-8 mb-8"
            items={Object.keys(flattenArticles[0])}
            subtitles={Object.values(flattenArticles[0])}
            bind:selectedItem={mapping[prop.key]}
            >{prop.isOptional ? '' : '*'} {prop.name}</Select
          >
        {/if}
      {/each}
      {#each categoryConfig.details as detailProp}
        {#if detailProp.isFixed}
          {#if detailProp.type === CategoryPropType.COLORS}
            <ColorPicker bind:color={mapping[`#${detailProp.key}`]} />
          {:else}
            <TextField bind:value={mapping[`#${detailProp.key}`]}
              >{detailProp.isOptional ? '' : '*'} {detailProp.name}</TextField
            >
          {/if}
        {:else}
          <Select
            className="mt-8 mb-8"
            items={Object.keys(flattenArticles[0])}
            subtitles={Object.values(flattenArticles[0])}
            bind:selectedItem={mapping[`details.${detailProp.key}`]}
            >{detailProp.isOptional ? '' : '*'} {detailProp.name}</Select
          >
        {/if}
      {/each}
      <Select
        className="mt-8 mb-8"
        items={Object.keys(flattenArticles[0])}
        subtitles={Object.values(flattenArticles[0])}
        bind:selectedItem={mapping[`candidateId`]}>* Unique article ID</Select
      >
    </Col>
    <Col cols={6}>
      <h5 class="text-h5 mb-4 mt-4">Preview</h5>
      <Row>
        {#each sampleItems as sampleItem}
          <Col cols={previewCols}>
            <ItemPreview item={sampleItem} {categoryConfig} />
          </Col>
        {/each}
      </Row>
    </Col>
  </Row>
{/if}
